/**
 * CALIDAD — INDICADORES (FASE 7.4)
 *
 * Una pestaña más de Calidad, no una aplicación aparte. Sirve para decidir:
 * tarjetas con el número (y siempre n/N en los porcentajes), una evolución
 * simple, distribuciones en barras con su valor, Top centros/ubicaciones y
 * la lista accionable de acciones vencidas. Sin gauges, sin donuts, sin
 * semáforos, sin «mejora/empeora»: el dato y su signo.
 *
 * Todo sale de `/api/calidad/kpis/*` (derivado en el servidor, con el
 * ámbito del usuario ya aplicado). `null` = «no hay datos suficientes».
 *
 * F11.4 — gestión diaria y seguimiento del cliente: «Situación actual» (lo que
 * hay que atender ahora), antigüedad, cliente, reaperturas y NC por centro y
 * por UBICACIÓN. Cada cifra lleva al listado de NC con el filtro que la
 * produce (mismas definiciones en el servidor), y el periodo, el cliente y el
 * centro viven en la URL: al volver del listado, la pestaña está como se dejó.
 *
 * PC-v2.2-C2.1: el periodo es el común de la analítica (`periodo-analitico.ts`: este mes, mes anterior, 30 y 90 días,
 * este año, personalizado) y uno inválido no se pide. Mientras llega la respuesta de otro filtro no se enseña la del
 * anterior. Al final, plegado, «Seguimiento del servicio» (`calidad-seguimiento.tsx`): las cifras del portal sobre
 * los clientes y centros del usuario, como bloque aparte; los indicadores internos de aquí no cambian.
 *
 * PC-v2.2-C2.3-D1: junto a los filtros, «Informe de gestión (Excel)» (E6, `exportar-informe-gestion.tsx`) con el mismo
 * periodo, cliente y centro de la pestaña; no cambia ningún filtro.
 */
import { useId, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api } from '../api.ts';
import { Cargando, Error as ErrorCaja, fecha, useDatos } from '../componentes/base.tsx';
import { ExportarInformeGestion } from '../componentes/exportar-informe-gestion.tsx';
import { SelectorPeriodo } from '../componentes/selector-periodo.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { errorDelRango, esClavePeriodo, MAX_DIAS_ANALITICA, rangoDe } from '../periodo-analitico.ts';
import { BloqueSeguimiento } from './calidad-seguimiento.tsx';

interface Cociente { pct: number | null; n: number; total: number }
interface Media { horas: number | null; n: number }
interface Tendencia { actual: number; anterior: number; variacionPct: number | null }
interface Resumen {
  periodo: { desde: string; hasta: string };
  incidenciasAbiertas: number; ncAbiertas: number; accionesPendientes: number; accionesVencidas: number;
  incidenciasVencidas: number; ncVencidas: number;
  incidenciasCreadas: number; incidenciasResueltas: number; ncCreadas: number; ncCerradas: number; accionesCompletadas: number;
  tiempoMedioResolucionIncidencia: Media; tiempoMedioCierreNc: Media; tiempoMedioCompletadoAccion: Media;
  tasaEscaladoNc: Cociente; cumplimientoIncidencias: Cociente; cumplimientoNc: Cociente; cumplimientoAcciones: Cociente;
  tendencia: Record<'incidenciasCreadas' | 'ncAbiertas' | 'accionesVencidas', Tendencia>;
  /** F11.4 */
  nc?: ResumenNc;
  /** Pre-F14.2: incidencias y NC por cada cien OT del mismo ámbito y periodo. */
  limpieza?: ActividadLimpieza;
}
interface ActividadLimpieza { otTotales: number; incidencias: Cociente; noConformidades: Cociente }
interface ResumenNc {
  activas: number; vencidas: number; sinResponsable: number; pendientesClasificar: number; sinAtender: number; reabiertasActivas: number;
  antiguedad: Tramo[];
  reaperturas: { total: number; porCliente: number; porEmpresa: number };
  tasaReapertura: Cociente;
  cerradasConReapertura: number;
  actividadCliente: { ncAbiertas: number; comentarios: number; evidencias: number; reaperturas: number; cierres: number };
}
interface Punto {
  inicio: string; incidenciasCreadas: number; incidenciasResueltas: number; ncAbiertas: number; ncCerradas: number; accionesCompletadas: number;
  ncGravesCriticas?: number; ncReaperturas?: number;
}
interface Tramo { clave: string; etiqueta: string; total: number }
interface FilaNcCentro { idCentro: string; centro: string; registradas: number; activas: number; cerradas: number; gravesCriticas: number; reabiertas: number; anterior: number }
interface FilaUbicacion {
  idCentro: string; centro: string; idUbicacion: string | null; ubicacion: string; total: number; activas: number; cerradas: number;
  reabiertas: number; gravesCriticas: number; pendientesClasificar: number; pesoPct: number; anterior: number; recurrente: boolean;
}
interface Distribuciones {
  incidenciasPorCentro: { idCentro: string; centro: string; total: number; abiertas: number; escaladas: number }[];
  ncPorCentro: FilaNcCentro[];
  ncDesdeIncidencia?: number;
  incidenciasPorNivel: Tramo[];
  ncPorGravedad: Tramo[];
  ncPorOrigen: Tramo[];
  repeticionesPorUbicacion: { idCentro: string; centro: string; ubicacion: string; tarea: string; total: number; abiertas: number; escaladas: number }[];
}
interface AccionVencida { id: string; descripcion: string; idNc: string; codigoNc: string; centro: string; responsableNombre: string | null; fechaObjetivo: string; diasRetraso: number }

const SIN_DATOS = 'No hay datos suficientes para este periodo.';
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** El límite de los indicadores internos en el servidor (`KPI_RANGO_DEMASIADO_AMPLIO`): tres años. */
const MAX_DIAS_KPI = 3 * 366 + 1;
/** Mientras se recarga con otro filtro, nada: nunca la respuesta del filtro anterior. */
const vigente = <T,>(d: { datos: T | null; cargando: boolean }): T | null => (d.cargando ? null : d.datos);
/** Granularidad que no genera cientos de puntos. */
function granularidad(desde: string, hasta: string): 'dia' | 'semana' | 'mes' {
  const dias = (Date.parse(hasta) - Date.parse(desde)) / 86_400_000 + 1;
  return dias <= 31 ? 'dia' : dias <= 120 ? 'semana' : 'mes';
}

/** Un decimal, coma decimal, sin «,0» sobrante: 42 → «42», 28.6 → «28,6». */
function num(v: number): string {
  return v.toFixed(1).replace('.', ',').replace(/,0$/, '');
}
export function textoDuracion(m: Media): string {
  if (m.horas === null) return '—';
  if (m.horas < 1) return 'menos de 1 h';
  return m.horas >= 48 ? `${num(m.horas / 24)} días` : `${num(m.horas)} h`;
}
export function textoCociente(c: Cociente): string {
  return c.pct === null ? '—' : `${num(c.pct)} % (${String(c.n)} de ${String(c.total)})`;
}
function textoVariacion(t: Tendencia): string {
  if (t.variacionPct === null) return 'sin periodo anterior comparable';
  const signo = t.variacionPct > 0 ? '+' : '';
  return `${signo}${num(t.variacionPct)} %`;
}

function Tarjeta({ titulo, valor, detalle, a, estado }: { titulo: string; valor: string; detalle?: string; a?: string; estado?: { volver: string } }): ReactNode {
  const cuerpo = (
    <>
      <div className="etiqueta-dato">{titulo}</div>
      <div className="cifra" style={{ fontSize: '1.6rem', fontWeight: 600 }}>{valor}</div>
      {detalle !== undefined && <div className="pequena tenue">{detalle}</div>}
    </>
  );
  return (
    <div className="panel kpi-tarjeta" style={{ flex: '1 1 160px', margin: 0 }}>
      <div className="panel-cuerpo">
        {a === undefined ? cuerpo : <Link to={a} state={estado} className="kpi-enlace" aria-label={`${titulo}: ${valor}. Ver el listado`}>{cuerpo}</Link>}
      </div>
    </div>
  );
}

/**
 * F11.4 — enlace a un listado filtrado de NC con el contexto de vuelta (la URL
 * de la pestaña de indicadores, con su periodo y centro).
 */
function useDrill(): (filtros: Record<string, string>) => string {
  const ubic = useLocation();
  const volver = `${ubic.pathname}${ubic.search}`;
  return (filtros) => {
    const q = new URLSearchParams({ tab: 'nc', ...filtros, volver });
    return `/calidad?${q.toString()}`;
  };
}

function Barras({ titulo, tramos, nota }: { titulo: string; tramos: readonly Tramo[]; nota?: string | undefined }): ReactNode {
  const max = Math.max(1, ...tramos.map((t) => t.total));
  const total = tramos.reduce((s, t) => s + t.total, 0);
  return (
    <div className="panel" style={{ flex: '1 1 260px', margin: 0 }}>
      <div className="panel-cabecera"><h3>{titulo}</h3></div>
      <div className="panel-cuerpo">
        {total === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={titulo}>
            {tramos.map((t) => (
              <li key={t.clave} style={{ marginBottom: 6 }}>
                <div className="fila" style={{ justifyContent: 'space-between' }}>
                  <span className="pequena">{t.etiqueta}</span>
                  <span className="pequena mono">{t.total}</span>
                </div>
                <div aria-hidden="true" style={{ background: 'var(--borde, #e3e8ee)', height: 8, borderRadius: 4 }}>
                  <div style={{ width: `${String((t.total / max) * 100)}%`, background: 'var(--marca, #0f6ea8)', height: 8, borderRadius: 4 }} />
                </div>
              </li>
            ))}
          </ul>
        )}
        {nota !== undefined && total > 0 && <p className="pequena tenue" style={{ marginBottom: 0 }}>{nota}</p>}
      </div>
    </div>
  );
}

/** F11.4 — antigüedad de las NC activas por tramos; cada tramo lleva a sus NC. */
function BloqueAntiguedad({ tramos, drill, ambito }: { tramos: readonly Tramo[]; drill: Drill; ambito: Record<string, string> }): ReactNode {
  const max = Math.max(1, ...tramos.map((t) => t.total));
  const total = tramos.reduce((s, t) => s + t.total, 0);
  return (
    <div className="panel">
      <div className="panel-cabecera"><h2>Antigüedad de las NC activas</h2></div>
      <div className="panel-cuerpo">
        {total === 0 ? <p className="pequena tenue">No hay NC activas.</p> : (
          <ul className="lista-antiguedad" aria-label="Antigüedad de las NC activas">
            {tramos.map((t) => (
              <li key={t.clave}>
                <Link to={drill({ ...ambito, antiguedad: t.clave })} className="fila-antiguedad" aria-label={`${t.etiqueta}: ${String(t.total)} NC`}>
                  <span className="pequena">{t.etiqueta}</span>
                  <span aria-hidden="true" className="barra-fondo"><span className="barra" style={{ width: `${String((t.total / max) * 100)}%` }} /></span>
                  <span className="pequena mono">{t.total}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * F11.4 — NC por UBICACIÓN: dónde se concentran los problemas. Ranking de las
 * NC REGISTRADAS en el periodo (la ubicación de la NC o, si no tiene, la sala
 * de su OT; «Sin ubicación» si ninguna). Cada fila lleva a sus NC.
 */
function BloqueUbicaciones({ datos, drill, periodo }: {
  datos: { datos: { filas: FilaUbicacion[] } | null; error: unknown; recargar: () => void };
  drill: Drill; periodo: { desde: string; hasta: string };
}): ReactNode {
  const filas = datos.datos?.filas ?? [];
  const recurrentes = filas.filter((f) => f.recurrente && f.idUbicacion !== null).length;
  return (
    <div className="panel">
      <div className="panel-cabecera"><h2>NC por ubicación</h2></div>
      <div className="panel-cuerpo">
        <p className="pequena tenue" style={{ marginTop: 0 }}>
          NC registradas en el periodo, de más a menos. «Recurrente»: dos o más NC en la misma ubicación en el periodo
          (repetición en un lugar, no una causa común demostrada).
          {recurrentes > 0 && ` ${String(recurrentes)} ubicación(es) recurrente(s).`}
        </p>
        {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
        {datos.datos !== null && (filas.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
          <div className="tabla-desliza">
            <table className="tabla-tarjetas kpi-tabla" aria-label="NC por ubicación">
              <thead>
                <tr><th>Ubicación</th><th>Centro</th><th>NC</th><th>Activas</th><th>Reabiertas</th><th>Graves o críticas</th><th>Peso en el centro</th><th>Periodo anterior</th></tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={`${f.idCentro}-${f.idUbicacion ?? 'sin'}`}>
                    <td className="celda-titulo">
                      <Link to={drill({ idCentro: f.idCentro, idUbicacion: f.idUbicacion ?? 'SIN', ubicacion: f.ubicacion, estado: 'TODAS', desde: periodo.desde, hasta: periodo.hasta })}>
                        {f.ubicacion}
                      </Link>
                      {f.recurrente && f.idUbicacion !== null && <> <span className="etiqueta aviso">Recurrente</span></>}
                    </td>
                    <td data-label="Centro">{f.centro}</td>
                    <td data-label="NC" className="mono">{f.total}</td>
                    <td data-label="Activas">{f.activas}</td>
                    <td data-label="Reabiertas">{f.reabiertas}</td>
                    <td data-label="Graves o críticas"><span>{f.gravesCriticas}{f.pendientesClasificar > 0 && <span className="pequena tenue"> (+{f.pendientesClasificar} sin clasificar)</span>}</span></td>
                    <td data-label="Peso en el centro">{num(f.pesoPct)} %</td>
                    <td data-label="Periodo anterior">{f.anterior}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PestanaIndicadores(): ReactNode {
  const idBase = useId();
  const { puede } = useSesion();
  const lista = useListaUrl({ tab: 'indicadores', periodo: '90', desde: '', hasta: '', idCliente: '', idCentro: '', seguimiento: '', agrupar: '' });
  const periodoUrl = lista.valor('periodo');
  const periodo = esClavePeriodo(periodoUrl) ? periodoUrl : '90';
  const idCliente = lista.valor('idCliente');
  const idCentro = lista.valor('idCentro');
  //  F11.4: «hoy» es la fecha LOCAL. Un periodo inválido (URL a mano, fechas a medio escribir) no se pide.
  const r = rangoDe(periodo, lista.valor('desde'), lista.valor('hasta'));
  const errorPeriodo = errorDelRango(r, MAX_DIAS_KPI);
  const q = new URLSearchParams({ desde: r.desde, hasta: r.hasta });
  if (idCliente !== '') q.set('idCliente', idCliente);
  if (idCentro !== '') q.set('idCentro', idCentro);
  const consulta = q.toString();
  const g = granularidad(r.desde, r.hasta);
  const drill = useDrill();
  //  Filtros de ámbito que acompañan a todo enlace al listado (el listado no filtra por cliente: el centro sí).
  const ambito: Record<string, string> = idCentro === '' ? {} : { idCentro };

  const centros = useDatos(() => api.get<{ filas: { id: string; descripcion: string; cliente: string; idCliente?: string }[] }>('/api/calidad/centros'), []);
  const clientes = [...new Map((centros.datos?.filas ?? []).filter((c) => c.idCliente !== undefined).map((c) => [c.idCliente!, c.cliente])).entries()];
  const centrosVisibles = (centros.datos?.filas ?? []).filter((c) => idCliente === '' || c.idCliente === idCliente);
  const pedir = <T,>(ruta: string): Promise<T | null> => (errorPeriodo === null ? api.get<T>(ruta) : Promise.resolve(null));
  const ubic = useDatos(() => pedir<{ filas: FilaUbicacion[] }>(`/api/calidad/kpis/ubicaciones?${consulta}`), [consulta, errorPeriodo]);
  const resumen = useDatos(() => pedir<Resumen>(`/api/calidad/kpis/resumen?${consulta}`), [consulta, errorPeriodo]);
  const serie = useDatos(() => pedir<{ puntos: Punto[] }>(`/api/calidad/kpis/serie?${consulta}&granularidad=${g}`), [consulta, g, errorPeriodo]);
  const dist = useDatos(() => pedir<Distribuciones>(`/api/calidad/kpis/distribuciones?${consulta}`), [consulta, errorPeriodo]);
  const datosResumen = vigente(resumen);
  const datosSerie = vigente(serie);
  const datosDist = vigente(dist);
  const vencidas = useDatos(() => api.get<{ filas: AccionVencida[] }>(`/api/calidad/kpis/acciones-vencidas?${idCentro === '' ? '' : `idCentro=${idCentro}`}`), [idCentro]);

  return (
    <>
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <SelectorPeriodo idBase={idBase} periodo={periodo} rango={r} error={errorPeriodo} fijar={(c) => lista.fijarVarios(c)} />
            {clientes.length > 1 && (
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-cl`}>Cliente</label>
                <select id={`${idBase}-cl`} value={idCliente} onChange={(e) => lista.fijarVarios({ idCliente: e.target.value, idCentro: '' })}>
                  <option value="">Todos</option>
                  {clientes.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
                </select>
              </div>
            )}
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-c`}>Centro</label>
              <select id={`${idBase}-c`} value={idCentro} onChange={(e) => lista.fijar('idCentro', e.target.value)}>
                <option value="">Todos</option>
                {centrosVisibles.map((c) => <option key={c.id} value={c.id}>{`${c.descripcion} · ${c.cliente}`}</option>)}
              </select>
            </div>
            <ExportarInformeGestion
              puede={puede} centros={centros.datos?.filas ?? []} cargando={centros.datos === null} idCliente={idCliente} idCentro={idCentro} rango={r}
            />
          </div>
          {errorPeriodo === null && (
            <p className="pequena tenue" style={{ marginBottom: 0 }}>
              Del {fecha(r.desde)} al {fecha(r.hasta)}. «Situación actual» es el estado de ahora (no depende del periodo); el resto, lo ocurrido en el periodo.
            </p>
          )}
        </div>
      </div>

      {errorPeriodo === null && resumen.cargando && <Cargando texto="Calculando indicadores…" />}
      {resumen.error !== null && <ErrorCaja error={resumen.error} reintentar={resumen.recargar} />}
      {datosResumen !== null && <BloqueResumen r={datosResumen} drill={drill} ambito={ambito} periodo={r} />}

      {errorPeriodo === null && <BloqueUbicaciones datos={{ ...ubic, datos: vigente(ubic) }} drill={drill} periodo={r} />}

      <div className="panel">
        <div className="panel-cabecera"><h2>Evolución</h2></div>
        <div className="panel-cuerpo">
          {serie.error !== null && <ErrorCaja error={serie.error} reintentar={serie.recargar} />}
          {errorPeriodo !== null && <p className="pequena tenue">{errorPeriodo}</p>}
          {errorPeriodo === null && serie.cargando && <Cargando texto="Calculando la evolución…" />}
          {datosSerie !== null && <TablaSerie puntos={datosSerie.puntos} g={g} />}
        </div>
      </div>

      {dist.error !== null && <ErrorCaja error={dist.error} reintentar={dist.recargar} />}
      {datosDist !== null && <BloqueDistribuciones d={datosDist} drill={drill} periodo={r} />}

      <div className="panel">
        <div className="panel-cabecera"><h2>Acciones correctivas vencidas</h2></div>
        <div className="panel-cuerpo">
          {vencidas.error !== null && <ErrorCaja error={vencidas.error} reintentar={vencidas.recargar} />}
          {vencidas.datos !== null && (vencidas.datos.filas.length === 0 ? (
            <p className="pequena tenue">No hay acciones correctivas vencidas.</p>
          ) : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas kpi-tabla">
                <caption className="sr-solo">Acciones correctivas vencidas, de la más atrasada a la menos</caption>
                <thead><tr><th>Acción</th><th>NC</th><th>Centro</th><th>Responsable</th><th>Fecha objetivo</th><th>Retraso</th></tr></thead>
                <tbody>
                  {vencidas.datos.filas.map((a) => (
                    <tr key={a.id}>
                      <td className="celda-titulo">{a.descripcion}</td>
                      <td data-label="NC"><Link to={`/calidad/no-conformidades/${a.idNc}`}>{a.codigoNc}</Link></td>
                      <td data-label="Centro">{a.centro}</td>
                      <td data-label="Responsable">{a.responsableNombre ?? '—'}</td>
                      <td data-label="Fecha objetivo">{fecha(a.fechaObjetivo)}</td>
                      <td data-label="Retraso">{a.diasRetraso} {a.diasRetraso === 1 ? 'día' : 'días'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>

      <BloqueSeguimiento
        rango={r} error={errorPeriodo ?? errorDelRango(r, MAX_DIAS_ANALITICA)} idCliente={idCliente} idCentro={idCentro}
        agrupar={lista.valor('agrupar')} fijarAgrupar={(v) => lista.fijar('agrupar', v)}
        abierto={lista.valor('seguimiento') === '1'} alCambiar={(a) => lista.fijar('seguimiento', a ? '1' : '')}
      />
    </>
  );
}

type Drill =(filtros: Record<string, string>) => string;

/** «7,0 %» (un decimal siempre, como en el informe de Services); sin OT, «—». */
export function textoPorcentajeOt(c: Cociente): string {
  return c.pct === null ? '—' : `${c.pct.toFixed(1).replace('.', ',')} %`;
}

/**
 * Pre-F14.2 — indicador de limpieza: OT del periodo y ámbito, y cuántas
 * incidencias y NC se registraron sobre ese mismo ámbito (en % de las OT).
 * Tres conceptos distintos: una línea NO_OK no es ni incidencia ni NC.
 */
function BloqueLimpieza({ l }: { l: ActividadLimpieza }): ReactNode {
  return (
    <section className="panel" aria-labelledby="kpi-limpieza">
      <div className="panel-cabecera"><h2 id="kpi-limpieza">Actividad de limpieza</h2></div>
      <div className="panel-cuerpo">
        <table className="kpi-limpieza" aria-describedby="kpi-limpieza-nota">
          <tbody>
            <tr><th scope="row">OT totales</th><td className="cifra-kpi">{l.otTotales}</td><td /></tr>
            <tr><th scope="row">Incidencias</th><td className="cifra-kpi">{l.incidencias.n}</td><td className="cifra-kpi">{textoPorcentajeOt(l.incidencias)}</td></tr>
            <tr><th scope="row">No conformidades</th><td className="cifra-kpi">{l.noConformidades.n}</td><td className="cifra-kpi">{textoPorcentajeOt(l.noConformidades)}</td></tr>
          </tbody>
        </table>
        <p id="kpi-limpieza-nota" className="pequena tenue" style={{ marginBottom: 0 }}>
          OT del periodo (sin anuladas) y las incidencias y NC registradas en él, con el mismo cliente y centro.
          El porcentaje es sobre las OT{l.otTotales === 0 ? ': sin OT en el periodo no se calcula' : ''}.
        </p>
      </div>
    </section>
  );
}

function BloqueResumen({ r, drill, ambito, periodo }: { r: Resumen; drill: Drill; ambito: Record<string, string>; periodo: { desde: string; hasta: string } }): ReactNode {
  const nc = r.nc;
  return (
    <>
      {r.limpieza !== undefined && <BloqueLimpieza l={r.limpieza} />}
      {nc !== undefined && (
        <section aria-labelledby="kpi-situacion" style={{ marginBottom: 12 }}>
          <h2 id="kpi-situacion" className="titulo-bloque">Situación actual de las NC</h2>
          <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
            <Tarjeta titulo="NC activas" valor={String(nc.activas)} detalle="abiertas y en tratamiento" a={drill({ ...ambito, estado: 'ACTIVAS' })} />
            <Tarjeta titulo="Sin responsable" valor={String(nc.sinResponsable)} detalle="activas que nadie ha tomado" a={drill({ ...ambito, sinResponsable: 'true' })} />
            <Tarjeta titulo="Pendientes de clasificar" valor={String(nc.pendientesClasificar)} detalle="abiertas por el cliente, sin gravedad oficial" a={drill({ ...ambito, estado: 'ACTIVAS', gravedad: 'PENDIENTE' })} />
            <Tarjeta titulo="El cliente espera respuesta" valor={String(nc.sinAtender)} detalle="escribió después de nuestra última respuesta visible" a={drill({ ...ambito, sinAtender: 'true' })} />
            <Tarjeta titulo="Vencidas" valor={String(nc.vencidas)} detalle="fecha de compromiso superada" a={drill({ ...ambito, vencidas: 'true' })} />
            <Tarjeta titulo="Reabiertas" valor={String(nc.reabiertasActivas)} detalle="activas que ya se habían cerrado" a={drill({ ...ambito, estado: 'ACTIVAS', reabiertas: 'true' })} />
          </div>
        </section>
      )}
      {nc !== undefined && <BloqueAntiguedad tramos={nc.antiguedad} drill={drill} ambito={ambito} />}
      <section aria-labelledby="kpi-incidencias" style={{ marginBottom: 12 }}>
        <h2 id="kpi-incidencias" className="titulo-bloque">Incidencias y acciones (ahora)</h2>
        <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Tarjeta titulo="Incidencias abiertas" valor={String(r.incidenciasAbiertas)} detalle={`${String(r.incidenciasVencidas)} vencida(s)`} a="/calidad?tab=incidencias" />
          <Tarjeta titulo="Acciones vencidas" valor={String(r.accionesVencidas)} detalle={`de ${String(r.accionesPendientes)} pendiente(s)`} a="/calidad?tab=acciones" />
        </div>
      </section>
      {nc !== undefined && (
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'stretch' }}>
          <div className="panel" style={{ flex: '1 1 300px', margin: 0 }}>
            <div className="panel-cabecera"><h2>Lo que ha hecho el cliente</h2></div>
            <div className="panel-cuerpo">
              <p className="pequena tenue" style={{ marginTop: 0 }}>En el periodo. Sin acuses de lectura: sólo lo que el cliente hizo.</p>
              <ul className="lista-kpi" aria-label="Actividad del cliente en el periodo">
                <li><span>NC abiertas por el cliente</span><Link to={drill({ ...ambito, estado: 'TODAS', origen: 'CLIENTE', desde: periodo.desde, hasta: periodo.hasta })}>{nc.actividadCliente.ncAbiertas}</Link></li>
                <li><span>Comentarios del cliente</span><span className="mono">{nc.actividadCliente.comentarios}</span></li>
                <li><span>Evidencias del cliente</span><span className="mono">{nc.actividadCliente.evidencias}</span></li>
                <li><span>Reaperturas del cliente</span><span className="mono">{nc.actividadCliente.reaperturas}</span></li>
                <li><span>Cierres del cliente</span><span className="mono">{nc.actividadCliente.cierres}</span></li>
              </ul>
            </div>
          </div>
          <div className="panel" style={{ flex: '1 1 300px', margin: 0 }}>
            <div className="panel-cabecera"><h2>Reaperturas</h2></div>
            <div className="panel-cuerpo">
              <ul className="lista-kpi" aria-label="Reaperturas">
                <li><span>Reaperturas en el periodo</span><span className="mono">{nc.reaperturas.total}</span></li>
                <li><span className="pequena suave">— por el cliente / por la empresa</span><span className="mono">{nc.reaperturas.porCliente} / {nc.reaperturas.porEmpresa}</span></li>
                <li><span>Tasa de reapertura</span><span>{nc.tasaReapertura.pct === null ? '—' : textoCociente(nc.tasaReapertura)}</span></li>
                <li><span>Reabiertas activas ahora</span><Link to={drill({ ...ambito, estado: 'ACTIVAS', reabiertas: 'true' })}>{nc.reabiertasActivas}</Link></li>
              </ul>
              <p className="pequena tenue" style={{ marginBottom: 0 }}>
                Tasa: de las NC con algún cierre en el periodo, cuántas se reabrieron después.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12 }} aria-label="Resultados del periodo">
        <Tarjeta
          titulo="Tiempo medio de resolución de incidencia"
          valor={textoDuracion(r.tiempoMedioResolucionIncidencia)}
          detalle={r.tiempoMedioResolucionIncidencia.n === 0 ? SIN_DATOS : `${String(r.tiempoMedioResolucionIncidencia.n)} resuelta(s)`}
        />
        <Tarjeta
          titulo="Tiempo medio de resolución de NC"
          valor={textoDuracion(r.tiempoMedioCierreNc)}
          detalle={r.tiempoMedioCierreNc.n === 0 ? SIN_DATOS
            : `de la apertura al cierre final · ${String(r.tiempoMedioCierreNc.n)} cerrada(s)${nc !== undefined && nc.cerradasConReapertura > 0 ? `, ${String(nc.cerradasConReapertura)} tras reabrirse` : ''}`}
        />
        <Tarjeta titulo="Incidencias escaladas a NC" valor={textoCociente(r.tasaEscaladoNc)} detalle={r.tasaEscaladoNc.pct === null ? SIN_DATOS : 'de las registradas en el periodo (sin canceladas)'} />
        <Tarjeta titulo="NC cerradas en plazo" valor={textoCociente(r.cumplimientoNc)} detalle={r.cumplimientoNc.pct === null ? SIN_DATOS : 'sólo NC con fecha de compromiso'} />
      </div>
      <div className="panel">
        <div className="panel-cabecera"><h2>Cumplimiento y tendencia</h2></div>
        <div className="panel-cuerpo">
          <div className="datos">
            <div className="dato"><div className="etiqueta-dato">Acciones completadas en plazo</div><div className="valor-dato">{r.cumplimientoAcciones.pct === null ? SIN_DATOS : textoCociente(r.cumplimientoAcciones)}</div></div>
            <div className="dato"><div className="etiqueta-dato">Incidencias resueltas en plazo</div><div className="valor-dato">{r.cumplimientoIncidencias.pct === null ? SIN_DATOS : textoCociente(r.cumplimientoIncidencias)}</div></div>
            <div className="dato"><div className="etiqueta-dato">Tiempo medio de una acción</div><div className="valor-dato">{r.tiempoMedioCompletadoAccion.n === 0 ? SIN_DATOS : textoDuracion(r.tiempoMedioCompletadoAccion)}</div></div>
          </div>
          <div className="tabla-desliza sep">
            <table className="tabla-tarjetas">
              <caption className="pequena tenue" style={{ textAlign: 'left' }}>Frente al periodo anterior de la misma duración</caption>
              <thead><tr><th>Indicador</th><th>Este periodo</th><th>Periodo anterior</th><th>Variación</th></tr></thead>
              <tbody>
                {([['Incidencias registradas', r.tendencia.incidenciasCreadas], ['NC registradas', r.tendencia.ncAbiertas], ['Acciones que vencieron', r.tendencia.accionesVencidas]] as const).map(([t, v]) => (
                  <tr key={t}>
                    <td className="celda-titulo">{t}</td><td data-label="Este periodo">{v.actual}</td>
                    <td data-label="Periodo anterior">{v.anterior}</td><td data-label="Variación">{textoVariacion(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function TablaSerie({ puntos, g }: { puntos: readonly Punto[]; g: 'dia' | 'semana' | 'mes' }): ReactNode {
  const hayDatos = puntos.some((p) => p.incidenciasCreadas + p.incidenciasResueltas + p.ncAbiertas + p.ncCerradas + p.accionesCompletadas > 0);
  if (!hayDatos) return <p className="pequena tenue">{SIN_DATOS}</p>;
  const max = Math.max(1, ...puntos.map((p) => p.incidenciasCreadas));
  //  F11.4: el mes con su nombre («Septiembre 2026»), no «2026-09».
  const etiqueta = (inicio: string): string =>
    g === 'mes' ? `${MESES[Number(inicio.slice(5, 7)) - 1] ?? ''} ${inicio.slice(0, 4)}` : g === 'semana' ? `Semana del ${fecha(inicio)}` : fecha(inicio);
  return (
    <div className="tabla-desliza">
      <table className="tabla-tarjetas tabla-evolucion">
        <caption className="sr-solo">Evolución por {g}</caption>
        <thead>
          <tr><th>{g === 'mes' ? 'Mes' : g === 'semana' ? 'Semana' : 'Día'}</th><th>Incidencias registradas</th><th>Incidencias resueltas</th><th>NC registradas</th><th>NC graves o críticas</th><th>NC cerradas</th><th>Reaperturas</th><th>Acciones completadas</th></tr>
        </thead>
        <tbody>
          {puntos.map((p) => (
            <tr key={p.inicio}>
              <td className="celda-titulo">{etiqueta(p.inicio)}</td>
              <td data-label="Incidencias registradas">
                <span className="fila" style={{ gap: 6 }}>
                  <span className="mono">{p.incidenciasCreadas}</span>
                  <span aria-hidden="true" style={{ display: 'inline-block', height: 8, width: `${String((p.incidenciasCreadas / max) * 80)}px`, background: 'var(--marca, #0f6ea8)', borderRadius: 4 }} />
                </span>
              </td>
              <td data-label="Incidencias resueltas">{p.incidenciasResueltas}</td>
              <td data-label="NC registradas">{p.ncAbiertas}</td>
              <td data-label="NC graves o críticas">{p.ncGravesCriticas ?? 0}</td>
              <td data-label="NC cerradas">{p.ncCerradas}</td>
              <td data-label="Reaperturas">{p.ncReaperturas ?? 0}</td>
              <td data-label="Acciones completadas">{p.accionesCompletadas}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BloqueDistribuciones({ d, drill, periodo }: { d: Distribuciones; drill: Drill; periodo: { desde: string; hasta: string } }): ReactNode {
  return (
    <>
      <div className="panel">
        <div className="panel-cabecera"><h2>NC por centro</h2></div>
        <div className="panel-cuerpo">
          <p className="pequena tenue" style={{ marginTop: 0 }}>Registradas, cerradas, graves/críticas y reabiertas: en el periodo. Activas: ahora.</p>
          {d.ncPorCentro.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas kpi-tabla" aria-label="NC por centro">
                <thead><tr><th>Centro</th><th>Registradas</th><th>Frente al periodo anterior</th><th>Activas ahora</th><th>Cerradas</th><th>Graves o críticas</th><th>Reabiertas</th></tr></thead>
                <tbody>
                  {d.ncPorCentro.map((c) => (
                    <tr key={c.idCentro}>
                      <td className="celda-titulo"><Link to={drill({ idCentro: c.idCentro, estado: 'TODAS', desde: periodo.desde, hasta: periodo.hasta })}>{c.centro}</Link></td>
                      <td data-label="Registradas">{c.registradas}</td>
                      <td data-label="Frente al periodo anterior">{textoVariacion({ actual: c.registradas, anterior: c.anterior, variacionPct: c.anterior === 0 ? null : Math.round(((c.registradas - c.anterior) * 1000) / c.anterior) / 10 })}</td>
                      <td data-label="Activas ahora"><Link to={drill({ idCentro: c.idCentro, estado: 'ACTIVAS' })}>{c.activas}</Link></td>
                      <td data-label="Cerradas">{c.cerradas}</td>
                      <td data-label="Graves o críticas">{c.gravesCriticas}</td>
                      <td data-label="Reabiertas">{c.reabiertas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'stretch' }}>
        <Barras titulo="Incidencias por nivel" tramos={d.incidenciasPorNivel} />
        <Barras titulo="NC por gravedad" tramos={d.ncPorGravedad} />
        <Barras titulo="NC por origen" tramos={d.ncPorOrigen} nota={d.ncDesdeIncidencia === undefined ? undefined : `${String(d.ncDesdeIncidencia)} nacida(s) al escalar una incidencia`} />
      </div>
      <div className="panel">
        <div className="panel-cabecera"><h2>Centros con más incidencias</h2></div>
        <div className="panel-cuerpo">
          {d.incidenciasPorCentro.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas">
                <thead><tr><th>Centro</th><th>Registradas</th><th>Siguen abiertas</th><th>Con NC</th></tr></thead>
                <tbody>
                  {d.incidenciasPorCentro.map((c) => (
                    <tr key={c.idCentro}>
                      <td className="celda-titulo">{c.centro}</td><td data-label="Registradas">{c.total}</td>
                      <td data-label="Siguen abiertas">{c.abiertas}</td><td data-label="Con NC">{c.escaladas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="panel">
        <div className="panel-cabecera"><h2>Ubicaciones y tareas con incidencias repetidas</h2></div>
        <div className="panel-cuerpo">
          <p className="pequena tenue">Dos o más incidencias en la misma ubicación y tarea durante el periodo. Es repetición, no una causa común demostrada.</p>
          {d.repeticionesPorUbicacion.length === 0 ? <p className="pequena tenue">No hay repeticiones en este periodo.</p> : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas">
                <thead><tr><th>Ubicación</th><th>Centro</th><th>Tarea</th><th>Incidencias</th><th>Siguen abiertas</th><th>Con NC</th></tr></thead>
                <tbody>
                  {d.repeticionesPorUbicacion.map((u) => (
                    <tr key={`${u.idCentro}-${u.ubicacion}-${u.tarea}`}>
                      <td className="celda-titulo">{u.ubicacion}</td><td data-label="Centro">{u.centro}</td><td data-label="Tarea">{u.tarea}</td>
                      <td data-label="Incidencias">{u.total}</td><td data-label="Siguen abiertas">{u.abiertas}</td><td data-label="Con NC">{u.escaladas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
